import { create } from "zustand";
import type { User } from "@/types";
import { authApi } from "@/api/endpoints";
import { tokenStore } from "@/api/client";

interface AuthState {
  user: User | null;
  status: "idle" | "loading" | "authenticated" | "unauthenticated";
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  bootstrap: () => Promise<void>;
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  status: "idle",

  login: async (identifier, password) => {
    const { data } = await authApi.login(identifier, password);
    tokenStore.set(data);
    try {
      const me = await authApi.me();
      if (me.data.role !== "admin") {
        tokenStore.clear();
        set({ user: null, status: "unauthenticated" });
        throw new Error("This account does not have admin access.");
      }
      set({ user: me.data, status: "authenticated" });
    } catch (err) {
      tokenStore.clear();
      set({ user: null, status: "unauthenticated" });
      throw err;
    }
  },

  logout: async () => {
    try {
      await authApi.logout();
    } catch {
      /* ignore network errors on logout */
    }
    tokenStore.clear();
    set({ user: null, status: "unauthenticated" });
  },

  bootstrap: async () => {
    if (!tokenStore.access) {
      set({ status: "unauthenticated" });
      return;
    }
    set({ status: "loading" });
    try {
      const me = await authApi.me();
      if (me.data.role !== "admin") {
        tokenStore.clear();
        set({ user: null, status: "unauthenticated" });
        return;
      }
      set({ user: me.data, status: "authenticated" });
    } catch {
      tokenStore.clear();
      set({ user: null, status: "unauthenticated" });
    }
  },
}));
